import _some from 'lodash/some';
import type { WSkuPreview } from '@zola/svc-web-api-ts-client';

type Attributes = Record<string, string>;
/**
 * Checks whether a given SKU matches all of the selected attributes.
 *
 * Note that all of the SKU's attributes don't have to match the selections,
 * but all the selections must match the SKU's attributes.
 */
const skuMatchesSelectedAttributes = (sku: WSkuPreview, selectedAttributes: Attributes) => {
  // The SKU does NOT match if any of the selected attributes have a different value than the SKU has for that attribute key.
  // If there are no selected attributes at all, the SKU will match.
  // If the SKU does not have a value for one of the selected attributes, it will not match.
  const skuDoesNotMatch = _some(Object.keys(selectedAttributes), (selectedAttributeKey) => {
    const selectedAttributeValue = selectedAttributes[selectedAttributeKey];
    const matchingSkuAttribute = sku.attribute_values?.filter(
      (skuAttr) => skuAttr.key === selectedAttributeKey
    )[0]; // if filter returns an empty array, index 0 will be undefined
    return !matchingSkuAttribute || matchingSkuAttribute.value_key !== selectedAttributeValue;
  });

  return !skuDoesNotMatch;
};

/**
 * Determines which single SKU matches the given attribute selections.
 * If multiple SKUs in the list match, then none match as we are only looking for one match.
 */
export const resolveMatchingSku = (skus: WSkuPreview[], selectedAttributes: Attributes) => {
  const matchingSkus = skus.filter((sku) => skuMatchesSelectedAttributes(sku, selectedAttributes));
  return matchingSkus.length === 1 ? matchingSkus[0] : null;
};
